import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Admin.css';
import { logout, getUserInfo, getAuthToken, isAdmin, canViewLoginActivity } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  full_admin: 'Full Admin',
  sales: 'Sales Dept',
  warehouse_manager: 'Warehouse Manager'
};

function roleLabel(role) {
  return ROLE_LABELS[role] || role || 'Unknown';
}

function Admin() {
  const navigate = useNavigate();
  const userInfo = getUserInfo();

  // Allowlisted non-super-admins only get the Login Activity tab
  const activityOnly = !isAdmin() && canViewLoginActivity();

  const [activeTab, setActiveTab] = useState(activityOnly ? 'activity' : 'users');

  // ── Users tab state ──────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '', password: '', email: '', full_name: '',
    role: 'warehouse_manager', warehouses: []
  });

  // ── External Access tab state ────────────────────────────────────
  const [externalUsers, setExternalUsers] = useState([]);
  const [showExtForm, setShowExtForm] = useState(false);
  const [editingExtUser, setEditingExtUser] = useState(null); // null = adding
  const [extForm, setExtForm] = useState({
    email: '', role: 'warehouse_manager', is_active: true, warehouses: []
  });
  const [extError, setExtError] = useState('');

  const [bulkRole, setBulkRole] = useState('warehouse_manager');

  // ── Login Activity tab state ─────────────────────────────────────
  const today = new Date().toISOString().slice(0, 10);
  const [activityRange, setActivityRange] = useState({
    start: new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10),
    end: today
  });
  const [activity, setActivity] = useState(null);
  const [activityView, setActivityView] = useState('summary');
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState('');

  const [showTransferForm, setShowTransferForm] = useState(false);
  const [transferTargetUser, setTransferTargetUser] = useState(null);
  const [transferPassword, setTransferPassword] = useState('');
  const [transferError, setTransferError] = useState('');

  useEffect(() => {
    if (!isAdmin()) {
      if (canViewLoginActivity()) { setLoading(false); loadActivity(); return; }
      navigate('/'); return;
    }
    loadData();
  }, [navigate]);

  // ── Load functions ───────────────────────────────────────────────

  const loadData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${getAuthToken()}` };
      const [usersRes, whseRes, extRes] = await Promise.all([
        axios.get(`${API_URL}/admin/users`, { headers }),
        axios.get(`${API_URL}/admin/warehouses`, { headers }),
        axios.get(`${API_URL}/admin/external-users`, { headers })
      ]);
      setUsers(usersRes.data.users);
      setWarehouses(whseRes.data.warehouses);
      setExternalUsers(extRes.data.users);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const refreshExtUsers = async () => {
    try {
      const headers = { Authorization: `Bearer ${getAuthToken()}` };
      const res = await axios.get(`${API_URL}/admin/external-users`, { headers });
      setExternalUsers(res.data.users);
    } catch (err) {
      console.error('Error refreshing external users:', err);
    }
  };

  const handleLogout = () => { logout(); window.location.href = '/login'; };

  // ── Local user handlers ──────────────────────────────────────────

  const handleAddUser = () => {
    setFormData({ username: '', password: '', email: '', full_name: '', role: 'warehouse_manager', warehouses: [] });
    setEditingUser(null);
    setShowAddUser(true);
  };

  const handleEditUser = (user) => {
    setFormData({
      username: user.username, password: '',
      email: user.email || '', full_name: user.full_name || '',
      role: user.role || 'warehouse_manager', warehouses: user.warehouses || []
    });
    setEditingUser(user);
    setShowAddUser(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      loadData();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await axios.put(`${API_URL}/admin/users/${user.id}`, { is_active: !user.is_active }, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      loadData();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update user');
    }
  };

  const handleSubmitUser = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${getAuthToken()}` };
      if (editingUser) {
        const updateData = { email: formData.email, full_name: formData.full_name, role: formData.role };
        if (formData.password) updateData.password = formData.password;
        await axios.put(`${API_URL}/admin/users/${editingUser.id}`, updateData, { headers });
        if (formData.email) {
          await axios.put(`${API_URL}/admin/users/${editingUser.id}/warehouses`,
            { warehouses: formData.warehouses }, { headers });
        }
      } else {
        const res = await axios.post(`${API_URL}/admin/users`, {
          username: formData.username, password: formData.password,
          email: formData.email, full_name: formData.full_name, role: formData.role
        }, { headers });
        if (formData.email && formData.warehouses.length > 0) {
          await axios.put(`${API_URL}/admin/users/${res.data.userId}/warehouses`,
            { warehouses: formData.warehouses }, { headers });
        }
      }
      setShowAddUser(false);
      loadData();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save user');
    }
  };

  const handleWarehouseToggle = (whse) => {
    setFormData(prev => ({
      ...prev,
      warehouses: prev.warehouses.includes(whse)
        ? prev.warehouses.filter(w => w !== whse)
        : [...prev.warehouses, whse]
    }));
  };

  // ── External user handlers ───────────────────────────────────────

  const openAddExtUser = () => {
    setEditingExtUser(null);
    setExtForm({ email: '', role: 'warehouse_manager', is_active: true, warehouses: [] });
    setExtError('');
    setShowExtForm(true);
  };

  const openEditExtUser = (user) => {
    setEditingExtUser(user);
    setExtForm({
      email: user.email,
      role: user.role || 'warehouse_manager',
      is_active: user.is_active,
      warehouses: [...user.warehouses]
    });
    setExtError('');
    setShowExtForm(true);
  };

  const handleSubmitExtUser = async (e) => {
    e.preventDefault();
    setExtError('');
    try {
      const headers = { Authorization: `Bearer ${getAuthToken()}` };
      if (editingExtUser) {
        await axios.put(`${API_URL}/admin/external-users/${encodeURIComponent(extForm.email)}`, {
          role: extForm.role,
          is_active: extForm.is_active,
          warehouses: extForm.warehouses
        }, { headers });
      } else {
        await axios.post(`${API_URL}/admin/external-users`, {
          email: extForm.email,
          role: extForm.role,
          warehouses: extForm.warehouses
        }, { headers });
      }
      setShowExtForm(false);
      refreshExtUsers();
    } catch (err) {
      setExtError(err.response?.data?.error || 'Failed to save user');
    }
  };

  const handleToggleExtActive = async (user) => {
    try {
      await axios.put(`${API_URL}/admin/external-users/${encodeURIComponent(user.email)}`, {
        is_active: !user.is_active
      }, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
      refreshExtUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update user');
    }
  };

  const handleDeleteExtUser = async (email) => {
    if (!window.confirm(`Remove ${email} and all their access? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/admin/external-users/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      refreshExtUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleExtWarehouseToggle = (whse) => {
    setExtForm(prev => ({
      ...prev,
      warehouses: prev.warehouses.includes(whse)
        ? prev.warehouses.filter(w => w !== whse)
        : [...prev.warehouses, whse]
    }));
  };

  const openTransferUser = (user) => {
    setTransferTargetUser(user);
    setTransferPassword('');
    setTransferError('');
    setShowTransferForm(true);
  };

  const handleTransferUser = async (e) => {
    e.preventDefault();
    setTransferError('');
    const username = transferTargetUser.email.split('@')[0].toLowerCase().replace(/[^a-z0-9._-]/g, '');
    try {
      const headers = { Authorization: `Bearer ${getAuthToken()}` };
      await axios.post(`${API_URL}/admin/users`, {
        username, password: transferPassword,
        email: transferTargetUser.email, role: transferTargetUser.role
      }, { headers });
      await axios.delete(
        `${API_URL}/admin/external-users/${encodeURIComponent(transferTargetUser.email)}`,
        { headers }
      );
      setShowTransferForm(false);
      loadData();
    } catch (err) {
      setTransferError(err.response?.data?.error || 'Transfer failed');
    }
  };

  const handleBulkStatus = async (is_active) => {
    if (!window.confirm(`${is_active ? 'Enable' : 'Disable'} all ${roleLabel(bulkRole)} external users?`)) return;
    try {
      await axios.put(`${API_URL}/admin/external-users/bulk`,
        { role: bulkRole, is_active },
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      );
      refreshExtUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Bulk action failed');
    }
  };

  // ── Login Activity handlers ──────────────────────────────────────

  const loadActivity = async () => {
    setActivityLoading(true);
    setActivityError('');
    try {
      const res = await axios.get(`${API_URL}/admin/login-activity`, {
        params: activityRange,
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      setActivity(res.data);
    } catch (err) {
      setActivityError(err.response?.data?.error || 'Failed to load login activity');
    } finally {
      setActivityLoading(false);
    }
  };

  const exportActivityCsv = () => {
    if (!activity) return;
    const esc = (v) => {
      const s = v === null || v === undefined ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const fmt = (d) => (d ? new Date(d).toLocaleString() : '');
    let header, lines;
    if (activityView === 'summary') {
      header = ['User', 'Role', 'Via Order Site', 'Direct Logins', 'Total Logins', 'Active Days', 'First Login', 'Last Login'];
      lines = activity.summary.map(s => [s.user, roleLabel(s.role), s.external_logins, s.direct_logins,
        s.total_logins, s.active_days, fmt(s.first_login), fmt(s.last_login)]);
    } else {
      header = ['Date/Time', 'Email', 'Username', 'Login Type', 'Status', 'Role', 'IP Address', 'Referer', 'Host'];
      lines = activity.rows.map(r => [fmt(r.login_at), r.email, r.username,
        r.auth_type === 'external' ? 'Order Site' : 'Direct', r.status, r.role, r.ip_address, r.referer, r.host]);
    }
    const csv = [header, ...lines].map(row => row.map(esc).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `login-activity-${activityView}-${activityRange.start}-to-${activityRange.end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isAdmin() && !activityOnly) return null;

  return (
    <div className="admin-container">
      <header className="admin-header">
        <div className="header-top">
          <div>
            <h1>Control Panel</h1>
            <h2>{activityOnly ? 'Login Activity' : 'User & Access Management'}</h2>
          </div>
          <div className="header-actions">
            <button className="back-btn" onClick={() => navigate('/')}>Back to Home</button>
            <span className="welcome-text">Welcome, {userInfo?.fullName || userInfo?.username}</span>
            <button className="logout-btn" onClick={handleLogout}>Logout</button>
          </div>
        </div>
      </header>

      <div className="admin-content">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : (
          <>
            {/* Tab Bar */}
            <div className="admin-tabs">
              {!activityOnly && (
                <>
                  <button className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
                    onClick={() => setActiveTab('users')}>
                    Direct Login Users
                  </button>
                  <button className={`admin-tab ${activeTab === 'access' ? 'active' : ''}`}
                    onClick={() => setActiveTab('access')}>
                    External Access Users
                  </button>
                </>
              )}
              <button className={`admin-tab ${activeTab === 'activity' ? 'active' : ''}`}
                onClick={() => { setActiveTab('activity'); if (!activity) loadActivity(); }}>
                Login Activity
              </button>
            </div>

            {/* ── Login Activity Tab ── */}
            {activeTab === 'activity' && (
              <>
                <div className="admin-toolbar">
                  <div className="toolbar-left">
                    <h3 className="section-label">Login Activity</h3>
                    <p className="section-desc">
                      Every login to this site, whether through the order site or a direct username/password login.
                    </p>
                  </div>
                </div>

                <div className="group-actions-bar">
                  <span className="group-actions-label">From:</span>
                  <input type="date" value={activityRange.start} max={activityRange.end}
                    onChange={e => setActivityRange({ ...activityRange, start: e.target.value })} />
                  <span className="group-actions-label">To:</span>
                  <input type="date" value={activityRange.end} min={activityRange.start}
                    onChange={e => setActivityRange({ ...activityRange, end: e.target.value })} />
                  <button className="bulk-enable-btn" onClick={loadActivity} disabled={activityLoading}>
                    {activityLoading ? 'Loading...' : 'Run'}
                  </button>
                  <select value={activityView} onChange={e => setActivityView(e.target.value)}
                    className="role-select group-role-select">
                    <option value="summary">Summary by User</option>
                    <option value="detail">Every Login</option>
                  </select>
                  <button className="add-user-btn" onClick={exportActivityCsv} disabled={!activity}>
                    Export CSV
                  </button>
                </div>

                {activityError && <div className="entry-error">{activityError}</div>}

                {activity && (() => {
                  const successes = activity.rows.filter(r => r.status === 'success');
                  const activeDays = new Set(successes.map(r => new Date(r.login_at).toDateString())).size;
                  return (
                    <p className="section-desc" style={{ margin: '10px 0' }}>
                      <strong>{successes.length}</strong> successful logins &middot;{' '}
                      <strong>{activity.summary.length}</strong> unique users &middot;{' '}
                      <strong>{activeDays}</strong> days with activity
                      {activity.rows.length >= 10000 && ' (detail limited to the most recent 10,000 rows)'}
                    </p>
                  );
                })()}

                {activity && (
                  <div className="users-table-container">
                    {activityView === 'summary' ? (
                      <table className="users-table">
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Role</th>
                            <th>Via Order Site</th>
                            <th>Direct</th>
                            <th>Total</th>
                            <th>Active Days</th>
                            <th>First Login</th>
                            <th>Last Login</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activity.summary.length === 0 ? (
                            <tr><td colSpan={8} style={{ textAlign: 'center', color: '#888', padding: '30px' }}>
                              No logins in this range
                            </td></tr>
                          ) : activity.summary.map(s => (
                            <tr key={s.user}>
                              <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>{s.user}</td>
                              <td>
                                <span className={`role-badge role-${s.role || 'warehouse_manager'}`}>
                                  {roleLabel(s.role)}
                                </span>
                              </td>
                              <td>{s.external_logins}</td>
                              <td>{s.direct_logins}</td>
                              <td><strong>{s.total_logins}</strong></td>
                              <td>{s.active_days}</td>
                              <td>{new Date(s.first_login).toLocaleString()}</td>
                              <td>{new Date(s.last_login).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <table className="users-table">
                        <thead>
                          <tr>
                            <th>Date/Time</th>
                            <th>User</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th>IP Address</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activity.rows.length === 0 ? (
                            <tr><td colSpan={5} style={{ textAlign: 'center', color: '#888', padding: '30px' }}>
                              No logins in this range
                            </td></tr>
                          ) : activity.rows.map(r => (
                            <tr key={r.id} className={r.status !== 'success' ? 'inactive' : ''}>
                              <td>{new Date(r.login_at).toLocaleString()}</td>
                              <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>{r.email || r.username}</td>
                              <td>{r.auth_type === 'external' ? 'Order Site' : 'Direct'}</td>
                              <td>
                                <span className={`status-badge ${r.status === 'success' ? 'active' : 'inactive'}`}>
                                  {r.status.replace('_', ' ')}
                                </span>
                              </td>
                              <td>{r.ip_address}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </>
            )}

            {/* ── Direct Login Users Tab ── */}
            {activeTab === 'users' && (
              <>
                <div className="admin-toolbar">
                  <div className="toolbar-left">
                    <h3 className="section-label">Direct Login Users</h3>
                    <p className="section-desc">Users who log in with a username and password directly on this site.</p>
                  </div>
                  <button className="add-user-btn" onClick={handleAddUser}>Add New User</button>
                </div>

                {showAddUser && (
                  <div className="user-form-overlay">
                    <div className="user-form-modal">
                      <h3>{editingUser ? 'Edit User' : 'Add New User'}</h3>
                      <form onSubmit={handleSubmitUser}>
                        <div className="form-group">
                          <label>Username *</label>
                          <input type="text" value={formData.username}
                            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                            disabled={!!editingUser} required />
                        </div>
                        <div className="form-group">
                          <label>Password {editingUser ? '(leave blank to keep current)' : '*'}</label>
                          <input type="password" value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            required={!editingUser} />
                        </div>
                        <div className="form-group">
                          <label>Email</label>
                          <input type="email" value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                        </div>
                        <div className="form-group">
                          <label>Full Name</label>
                          <input type="text" value={formData.full_name}
                            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} />
                        </div>
                        <div className="form-group">
                          <label>Role *</label>
                          <select value={formData.role}
                            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                            className="role-select">
                            <option value="super_admin">Super Admin</option>
                            <option value="full_admin">Full Admin</option>
                            <option value="sales">Sales Dept</option>
                            <option value="warehouse_manager">Warehouse Manager</option>
                          </select>
                        </div>
                        {formData.email && (
                          <div className="form-group">
                            <label>Warehouse Assignments</label>
                            <div className="admin-warehouse-grid">
                              {warehouses.map(whse => (
                                <label key={whse} className="warehouse-checkbox">
                                  <input type="checkbox"
                                    checked={formData.warehouses.includes(whse)}
                                    onChange={() => handleWarehouseToggle(whse)} />
                                  {whse}
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="form-actions">
                          <button type="submit" className="save-btn">
                            {editingUser ? 'Update User' : 'Create User'}
                          </button>
                          <button type="button" className="cancel-btn" onClick={() => setShowAddUser(false)}>
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                <div className="users-table-container">
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th>Username</th>
                        <th>Email</th>
                        <th>Full Name</th>
                        <th>Role</th>
                        <th>Warehouses</th>
                        <th>Status</th>
                        <th>Last Login</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(user => (
                        <tr key={user.id} className={!user.is_active ? 'inactive' : ''}>
                          <td>{user.username}</td>
                          <td>{user.email || '-'}</td>
                          <td>{user.full_name || '-'}</td>
                          <td>
                            <span className={`role-badge role-${user.role || 'warehouse_manager'}`}>
                              {roleLabel(user.role)}
                            </span>
                          </td>
                          <td>
                            {user.warehouses?.length > 0 ? (
                              <div className="warehouse-badges">
                                {user.warehouses.map(w => <span key={w} className="warehouse-badge">{w}</span>)}
                              </div>
                            ) : <span className="no-warehouses">None</span>}
                          </td>
                          <td>
                            <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                              {user.is_active ? 'Active' : 'Disabled'}
                            </span>
                          </td>
                          <td>{user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}</td>
                          <td>
                            <div className="action-buttons">
                              <button className="edit-btn" onClick={() => handleEditUser(user)}>Edit</button>
                              <button className="toggle-btn" onClick={() => handleToggleActive(user)}>
                                {user.is_active ? 'Disable' : 'Enable'}
                              </button>
                              <button className="delete-btn" onClick={() => handleDeleteUser(user.id)}>Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* ── External Access Users Tab ── */}
            {activeTab === 'access' && (
              <>
                <div className="admin-toolbar">
                  <div className="toolbar-left">
                    <h3 className="section-label">External Access Users</h3>
                    <p className="section-desc">
                      Users who authenticate via the other app (email-based). Set their role, enable/disable access, and manage which warehouses they can see.
                    </p>
                  </div>
                  <button className="add-user-btn" onClick={openAddExtUser}>Add External User</button>
                </div>

                {/* Add / Edit External User Modal */}
                {showExtForm && (
                  <div className="user-form-overlay">
                    <div className="user-form-modal">
                      <h3>{editingExtUser ? `Edit: ${editingExtUser.email}` : 'Add External User'}</h3>
                      <form onSubmit={handleSubmitExtUser}>
                        <div className="form-group">
                          <label>Email Address *</label>
                          <input type="email" value={extForm.email}
                            onChange={(e) => setExtForm({ ...extForm, email: e.target.value })}
                            disabled={!!editingExtUser}
                            placeholder="user@example.com"
                            required />
                        </div>

                        <div className="form-group">
                          <label>Role *</label>
                          <select value={extForm.role}
                            onChange={(e) => setExtForm({ ...extForm, role: e.target.value })}
                            className="role-select">
                            <option value="super_admin">Super Admin</option>
                            <option value="full_admin">Full Admin</option>
                            <option value="sales">Sales Dept</option>
                            <option value="warehouse_manager">Warehouse Manager</option>
                          </select>
                        </div>

                        {editingExtUser && (
                          <div className="form-group checkbox-group">
                            <label>
                              <input type="checkbox"
                                checked={extForm.is_active}
                                onChange={(e) => setExtForm({ ...extForm, is_active: e.target.checked })} />
                              Account Active (uncheck to disable)
                            </label>
                          </div>
                        )}

                        <div className="form-group">
                          <div className="warehouse-label-row">
                            <label>Warehouse Access</label>
                            <div className="warehouse-quick-btns">
                              <button type="button" className="whse-quick-btn"
                                onClick={() => setExtForm(prev => ({ ...prev, warehouses: [...warehouses] }))}>
                                Select All
                              </button>
                              <button type="button" className="whse-quick-btn"
                                onClick={() => setExtForm(prev => ({ ...prev, warehouses: [] }))}>
                                Clear All
                              </button>
                            </div>
                          </div>
                          <div className="admin-warehouse-grid">
                            {warehouses.map(whse => (
                              <label key={whse} className="warehouse-checkbox">
                                <input type="checkbox"
                                  checked={extForm.warehouses.includes(whse)}
                                  onChange={() => handleExtWarehouseToggle(whse)} />
                                {whse}
                              </label>
                            ))}
                          </div>
                        </div>

                        {extError && <div className="entry-error">{extError}</div>}

                        <div className="form-actions">
                          <button type="submit" className="save-btn">
                            {editingExtUser ? 'Save Changes' : 'Add User'}
                          </button>
                          <button type="button" className="cancel-btn" onClick={() => setShowExtForm(false)}>
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                {/* Transfer to Direct Login modal */}
                {showTransferForm && (
                  <div className="user-form-overlay">
                    <div className="user-form-modal">
                      <h3>Transfer to Direct Login</h3>
                      <form onSubmit={handleTransferUser}>
                        <div className="form-group">
                          <label>Email</label>
                          <input type="text" value={transferTargetUser?.email} disabled />
                        </div>
                        <div className="form-group">
                          <label>Username (auto-assigned)</label>
                          <input type="text"
                            value={transferTargetUser?.email.split('@')[0].toLowerCase().replace(/[^a-z0-9._-]/g, '')}
                            disabled />
                        </div>
                        <div className="form-group">
                          <label>Password to send them *</label>
                          <input type="text" value={transferPassword}
                            onChange={e => setTransferPassword(e.target.value)}
                            placeholder="Type a password you will share with this user"
                            required />
                        </div>
                        {transferError && <div className="entry-error">{transferError}</div>}
                        <div className="form-actions">
                          <button type="submit" className="save-btn">Transfer User</button>
                          <button type="button" className="cancel-btn" onClick={() => setShowTransferForm(false)}>Cancel</button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                {/* Group Actions */}
                <div className="group-actions-bar">
                  <span className="group-actions-label">Group Actions:</span>
                  <select value={bulkRole} onChange={e => setBulkRole(e.target.value)} className="role-select group-role-select">
                    <option value="super_admin">Super Admin</option>
                    <option value="full_admin">Full Admin</option>
                    <option value="sales">Sales Dept</option>
                    <option value="warehouse_manager">Warehouse Manager</option>
                  </select>
                  <button className="bulk-disable-btn" onClick={() => handleBulkStatus(false)}>Disable Group</button>
                  <button className="bulk-enable-btn" onClick={() => handleBulkStatus(true)}>Enable Group</button>
                </div>

                <div className="users-table-container">
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Warehouses</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {externalUsers.length === 0 ? (
                        <tr>
                          <td colSpan={5} style={{ textAlign: 'center', color: '#888', padding: '30px' }}>
                            No external users found
                          </td>
                        </tr>
                      ) : (
                        externalUsers.map(user => (
                          <tr key={user.email} className={!user.is_active ? 'inactive' : ''}>
                            <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>{user.email}</td>
                            <td>
                              <span className={`role-badge role-${user.role || 'warehouse_manager'}`}>
                                {roleLabel(user.role)}
                              </span>
                            </td>
                            <td>
                              <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                                {user.is_active ? 'Active' : 'Disabled'}
                              </span>
                            </td>
                            <td>
                              {user.warehouses?.length > 0 ? (
                                <div className="warehouse-badges">
                                  {user.warehouses.map(w => <span key={w} className="warehouse-badge">{w}</span>)}
                                </div>
                              ) : <span className="no-warehouses">None</span>}
                            </td>
                            <td>
                              <div className="action-buttons">
                                <button className="edit-btn" onClick={() => openEditExtUser(user)}>Edit</button>
                                <button className="toggle-btn" onClick={() => handleToggleExtActive(user)}>
                                  {user.is_active ? 'Disable' : 'Enable'}
                                </button>
                                <button className="transfer-btn" onClick={() => openTransferUser(user)}>Transfer</button>
                                <button className="delete-btn" onClick={() => handleDeleteExtUser(user.email)}>
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Admin;
